import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;

@Component({
    selector: 'app-wizy-icon',
    standalone: true,
    imports: [CommonModule],
    template: '',
    styleUrls: ['./icon.component.scss'],
})
export class WizyIconComponent implements OnInit, OnChanges, OnDestroy {
    @Input() name: string = '';
    @Input() size: IconSize = 'md';
    @Input() color?: string;
    @Input() ariaLabel?: string;
    @Input() title?: string;

    private http = inject(HttpClient);
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);
    private destroy$ = new Subject<void>();
    private iconCache = new Map<string, string>();

    ngOnInit(): void {
        // Initialize styles and load icon on init
        this.updateStyles();
        if (this.name) {
            this.loadSvgIcon(this.name);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['name'] && this.name) {
            this.loadSvgIcon(this.name);
        }

        if (changes['size'] || changes['color']) {
            this.updateStyles();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadSvgIcon(name: string): void {
        // Check cache first
        if (this.iconCache.has(name)) {
            this.renderSvg(this.iconCache.get(name)!);
            return;
        }

        const path = `assets/icons/${name}.svg`;
        this.http
            .get(path, { responseType: 'text' })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (svg) => {
                    // Cache the icon
                    this.iconCache.set(name, svg);
                    this.renderSvg(svg);
                },
                error: (err) => {
                    console.warn(`Icon "${name}" not found at ${path}`, err);
                    this.renderer.setProperty(this.el.nativeElement, 'innerHTML', '');
                },
            });
    }

    private renderSvg(svg: string): void {
        const div = this.renderer.createElement('div');
        this.renderer.setProperty(div, 'innerHTML', svg);
        const svgElement = div.querySelector('svg');

        if (svgElement) {
            // Clear and append the SVG
            const nativeElement = this.el.nativeElement;
            this.renderer.setProperty(nativeElement, 'innerHTML', '');
            this.renderer.appendChild(nativeElement, svgElement);
        }
    }

    private updateStyles(): void {
        // Update classes on host element
        const sizeClass = typeof this.size === 'string' ? `wizy-icon--${this.size}` : '';

        // Remove all size classes first
        ['xs', 'sm', 'md', 'lg', 'xl'].forEach((size) => {
            this.renderer.removeClass(this.el.nativeElement, `wizy-icon--${size}`);
        });

        // Add the current size class if it's a string
        if (sizeClass) {
            this.renderer.addClass(this.el.nativeElement, sizeClass);
        }

        // Apply inline styles for custom size and color
        if (typeof this.size === 'number') {
            this.renderer.setStyle(this.el.nativeElement, 'width', `${this.size}px`);
            this.renderer.setStyle(this.el.nativeElement, 'height', `${this.size}px`);
        } else {
            this.renderer.removeStyle(this.el.nativeElement, 'width');
            this.renderer.removeStyle(this.el.nativeElement, 'height');
        }

        if (this.color) {
            this.renderer.setStyle(this.el.nativeElement, 'color', this.color);
        } else {
            this.renderer.removeStyle(this.el.nativeElement, 'color');
        }

        // Apply aria attributes
        if (this.ariaLabel) {
            this.renderer.setAttribute(this.el.nativeElement, 'aria-label', this.ariaLabel);
            this.renderer.setAttribute(this.el.nativeElement, 'role', 'img');
            this.renderer.setAttribute(this.el.nativeElement, 'aria-hidden', 'false');
        } else {
            this.renderer.setAttribute(this.el.nativeElement, 'role', 'presentation');
            this.renderer.setAttribute(this.el.nativeElement, 'aria-hidden', 'true');
        }

        if (this.title) {
            this.renderer.setAttribute(this.el.nativeElement, 'title', this.title);
        }
    }
}
